import { useCallback, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import type { JSONContent } from '@tiptap/core';
import type { LinkBlock, LinkTitleLevel } from '@course-authoring/shared-types';
import { sanitizeRichTextContent, useTheme } from '@course-authoring/shared-types';
import { useIsMobileViewport } from '@course-authoring/ui-blocks';
import { InteractionRichTextField } from './InteractionRichTextField';
import { createTokenRichTextToolbarStyles } from './interactionEditorUtils';
import { TitleLevelSelector, getTitleInputStyle } from './TitleLevelSelector';

type FieldName = 'title' | 'description' | 'linkText' | 'linkUrl';

export interface LinkEditorProps {
  block: LinkBlock;
  onChange: (updates: Partial<LinkBlock>) => void;
  autoFocus?: boolean;
  translationMode?: boolean;
}

export function LinkEditor({
  block,
  onChange,
  autoFocus = false,
  translationMode = false,
}: LinkEditorProps): React.ReactElement {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedField, setFocusedField] = useState<FieldName | null>(null);

  const titleLevel = block.titleLevel ?? 'h2';
  const toolbarStyles = useMemo(() => createTokenRichTextToolbarStyles(tokens), [tokens]);
  const sanitizedDescription = useMemo(
    () =>
      !block.description || typeof block.description === 'string'
        ? { type: 'doc' as const, content: [{ type: 'paragraph' as const }] }
        : sanitizeRichTextContent(block.description as JSONContent),
    [block.description]
  );

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ title: e.target.value });
    },
    [onChange]
  );

  const handleLinkTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ linkText: e.target.value });
    },
    [onChange]
  );

  const handleLinkUrlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ linkUrl: e.target.value });
    },
    [onChange]
  );

  // Live hint only, not save-blocking validation: warns while the URL is
  // empty (learners would have nothing to open) and clears the moment any
  // value is entered — no format/protocol checking here, unlike Policy
  // Acknowledgement's URL field, since the ticket scopes this to emptiness.
  const showMissingUrlWarning = !block.linkUrl?.trim();

  const styles: Record<string, CSSProperties> = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
      width: '100%',
      fontFamily: tokens.typography.fontFamily,
    },
    section: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
    },
    label: {
      fontSize: '13px',
      fontWeight: 600,
      color: tokens.text.muted,
      fontFamily: tokens.typography.fontFamily,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
    },
    titleInput: getTitleInputStyle(titleLevel, tokens),
    descriptionWrapper: {
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: focusedField === 'description'
        ? tokens.ui.selectionBorder
        : tokens.borders.default,
      borderRadius: tokens.radius.sm,
      padding: tokens.spacing.md,
      transition: `border-color ${tokens.ui.transitionDefault}`,
    },
    textInput: {
      fontSize: '14px',
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: tokens.borders.default,
      borderRadius: tokens.radius.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      width: '100%',
      boxSizing: 'border-box' as const,
      outline: 'none',
    },
    urlInput: {
      fontSize: '14px',
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: tokens.borders.default,
      borderRadius: tokens.radius.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      width: '100%',
      boxSizing: 'border-box' as const,
      outline: 'none',
    },
    warningBanner: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: tokens.spacing.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      borderRadius: tokens.radius.md,
      border: `1px solid ${tokens.ui.workflowDraft}`,
      backgroundColor: `color-mix(in srgb, ${tokens.ui.workflowDraft} 12%, transparent)`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '13px',
      color: tokens.text.primary,
      lineHeight: 1.4,
    },
    warningIcon: {
      fontSize: '14px',
      color: tokens.ui.workflowDraft,
      marginTop: '2px',
      flexShrink: 0,
    },
  };

  return (
    <div ref={containerRef} style={styles.container}>
      <div style={styles.section}>
        {!translationMode ? <TitleLevelSelector
          currentLevel={titleLevel}
          onSelectLevel={(level) => onChange({ titleLevel: level as LinkTitleLevel })}
          tokens={tokens}
        /> : null}
        <input
          type="text"
          value={block.title ?? ''}
          onChange={handleTitleChange}
          onFocus={() => setFocusedField('title')}
          onBlur={() => setFocusedField(null)}
          placeholder="Enter title (optional)..."
          style={styles.titleInput}
          autoFocus={autoFocus}
        />
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Paragraph</label>
        <div style={styles.descriptionWrapper}>
          <InteractionRichTextField
            content={sanitizedDescription}
            placeholder="Enter paragraph text (optional)..."
            isFocused={focusedField === 'description'}
            onFocus={() => setFocusedField('description')}
            onBlur={() => setFocusedField(null)}
            onContentChange={(content) => onChange({ description: content })}
            autoFocus={false}
            containerRef={containerRef}
            toolbarPreset="full"
            toolbarStyles={toolbarStyles}
            editorInlineStyle={`outline: none; min-height: ${isMobile ? '40px' : '60px'};`}
            shouldRerenderOnTransaction
          />
        </div>
      </div>

      {!translationMode ? <>
        <div style={styles.section}>
          <label style={styles.label}>Link Text</label>
          <input
            type="text"
            value={block.linkText ?? ''}
            onChange={handleLinkTextChange}
            onFocus={() => setFocusedField('linkText')}
            onBlur={() => setFocusedField(null)}
            placeholder="Enter link text..."
            style={styles.textInput}
          />
        </div>

        <div style={styles.section}>
          <label style={styles.label}>Link URL</label>
          <input
            type="url"
            value={block.linkUrl ?? ''}
            onChange={handleLinkUrlChange}
            onFocus={() => setFocusedField('linkUrl')}
            onBlur={() => setFocusedField(null)}
            placeholder="https://example.com"
            style={styles.urlInput}
          />
          {showMissingUrlWarning && (
            <div style={styles.warningBanner} role="alert">
              <span style={styles.warningIcon} aria-hidden="true">
                <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: 14 }} />
              </span>
              <span>
                You must enter a URL or learners will be unable to proceed.
              </span>
            </div>
          )}
        </div>
      </> : (
        block.linkUrl ? (
          <div style={styles.section}>
            <span style={styles.label}>Link</span>
            <a href={block.linkUrl} target="_blank" rel="noreferrer">
              {block.linkText || block.linkUrl}
            </a>
          </div>
        ) : null
      )}
    </div>
  );
}

LinkEditor.displayName = 'LinkEditor';
